/**
 * Home: pairing status, the two identities, and the in-context notification ask — shown from a
 * card the user taps right after pairing succeeds, never on load.
 *
 * ONE BANDED CARD PER ACTION, AND NOTHING ELSE WEARS A BAND.
 *
 * The header band is the round's loudest object, and this screen had four of them stacked: the
 * pairing moment, the preference sheet, the notification ask, the settings list and the "how this
 * works" paragraph all shouted at the same volume, so the eye landed on nothing. The rule now is
 * the one the band was invented for — a band is a TITLE on a card that ASKS for something:
 *
 *   pairing            a moment, and the one card with a garland and no band at all
 *   preference sheet   an action
 *   notifications      an action
 *   settings, Look,    plain sections: a caption heading on a bare sheet, because a list of
 *   how Hunsy works    controls and a paragraph are things you read, not things that ask
 */
import { PanelSkeleton } from '@/components/load-state';
import { ThemeToggle } from '@/components/nav';
import { Panel } from '@/components/panel';
import { Segmented, type SegmentedOption } from '@/components/segmented';
import { WheelPicker } from '@/components/wheel-picker';
import { Switch } from '@/components/switch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { api } from '@/api';
import { cn } from '@/lib/utils';
import { otherPartner, partnerTint } from '@/lib/partner';
import { currentPermission, enablePush, isSubscribed, pushSupported, sendTestPush } from '@/push';
import { useStore, type Look } from '@/store';
import * as React from 'react';
import { Pressable, View } from 'react-native';

interface NotificationSettings {
  muted: boolean;
  quietStart: string;
  quietEnd: string;
  /**
   * What the server would use for a partner who has set nothing — sent on every GET so this
   * screen never has to guess, and what "Reset to default" writes back.
   */
  defaults: { quietStart: string; quietEnd: string };
}

/**
 * The card's shape when the server could not be reached at all.
 *
 * A copy of `api/src/config/notifications.config.ts`'s own defaults, and the one place in the
 * client that holds them. It is not a second source of truth: every successful GET overwrites it,
 * and it exists only so a failed load renders the CARD — with its controls off and a way to try
 * again — rather than deleting the notification settings, the quiet window and the look switch off
 * the screen with nothing to say why.
 */
const FALLBACK: NotificationSettings = {
  muted: false,
  quietStart: '22:00',
  quietEnd: '09:00',
  defaults: { quietStart: '22:00', quietEnd: '09:00' },
};

/**
 * The two rooms, as the Look section offers them.
 *
 * Module scope rather than inline: the list is fixed by the round — a segmented control is the
 * right shape precisely because the set cannot grow a third option later — and a fresh array
 * every render is a new prop identity for nothing.
 */
const THEME_OPTIONS: readonly SegmentedOption<Look>[] = [
  { value: 'bouquet', label: 'Bouquet' },
  { value: 'tuxedo', label: 'Tuxedo' },
];

/**
 * A SETTINGS LIST's own width, on a desktop.
 *
 * Label left and control right is only an association while the two are near each other: inside
 * the 880 column the Theme label sat about a thousand pixels from the segmented control it names,
 * which is two facts on one line rather than one row. The rows stop at a readable measure and the
 * rest of the column is air.
 */
const ROWS_CLASS = 'w-full max-w-[560px]';

/** Two characters, not one — a couple can easily share an initial. */
function initials(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed[0]!.toUpperCase() + trimmed.slice(1, 2).toLowerCase() : '?';
}

/** Minutes past midnight. Tolerant of anything the server would have rejected anyway. */
function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  return (Number(h) || 0) * 60 + (Number(m) || 0);
}

/**
 * How long the quiet window runs, across midnight.
 *
 * Zero is a real answer rather than a broken one: the server reads `start === end` as
 * `mins >= s && mins < e`, which is never true — so the two ends meeting means there are no quiet
 * hours at all, and the row says that in words rather than printing "0 h" at somebody.
 */
function spanMinutes(start: string, end: string): number {
  return (minutesOf(end) - minutesOf(start) + 1440) % 1440;
}

function spanLabel(mins: number): string {
  if (mins === 0) return 'no quiet window';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m}m`;
  return m ? `${h} h ${m}m` : `${h} h`;
}

/** A plain section: a caption heading on a bare sheet, and no band. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent>
        <Text variant="label">{title}</Text>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * One settings row: the name of the thing on the left, the thing itself on the right.
 *
 * `first` suppresses the top rule. The caption above the list is already its top edge, and two
 * rules four pixels apart read as a rendering fault rather than as a list.
 */
function Row({ label, first, children }: { label: React.ReactNode; first?: boolean; children: React.ReactNode }) {
  return (
    <View className={cn('flex-row items-center gap-3 py-3', !first && 'border-border border-t')}>
      <View className="min-w-0 flex-1">
        {typeof label === 'string' ? <Text className="text-body font-medium">{label}</Text> : label}
      </View>
      {children}
    </View>
  );
}

export function HomeScreen() {
  const { me, identities, setSession, rename, openOverlay, fail } = useStore();
  /**
   * The look is read here for two reasons at once: the Look section is where it is set, and the
   * pairing card's `&` is cut differently by each sheet — Bouquet's is a gold italic, Tuxedo's an
   * upright grey. Selectors rather than the destructured store above so these reads say plainly
   * which state this screen depends on.
   */
  const look = useStore((s) => s.look);
  const setLook = useStore((s) => s.setLook);
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  /** Set once by `session.boot` on a fresh `?key=` pairing — the moment the ask is FOR. */
  const justPaired = useStore((s) => s.justPaired);

  const [settings, setSettings] = React.useState<NotificationSettings | null>(null);
  const [nameDraft, setNameDraft] = React.useState<string | null>(null);
  const [permission, setPermission] = React.useState<string>('default');
  const [subscribed, setSubscribed] = React.useState(false);
  /**
   * One result string per card, not one shared between them.
   *
   * "Subscribed — your partner's devices just got a test ping." belongs to the ask, and it was
   * rendered under whichever of the two cards happened to be on screen afterwards — so enabling
   * notifications printed the ask's sentence under "Notifications on", where it read as the
   * result of a Ping nobody had pressed.
   */
  const [askResult, setAskResult] = React.useState('');
  const [pingResult, setPingResult] = React.useState('');
  const [asking, setAsking] = React.useState(false);

  /** The quiet-hours picker: open, and the two ends as drafts until Done commits both at once. */
  const [picking, setPicking] = React.useState(false);
  const [fromDraft, setFromDraft] = React.useState('00:00');
  const [untilDraft, setUntilDraft] = React.useState('00:00');

  /**
   * The settings card knows whether it has ever loaded, exactly as a polled panel does.
   *
   * A bare `catch {}` here meant a 500 rendered as no card at all: the notification controls, the
   * quiet hours and the theme switch simply were not on the screen, with nothing to say why or to
   * ask again. It is a lighter version of `usePoll`'s two facts, because this one does not poll.
   */
  const [settingsError, setSettingsError] = React.useState<string | null>(null);

  const loadSettings = React.useCallback(async () => {
    setSettingsError(null);
    try {
      setSettings(await api.get<NotificationSettings>('/api/settings/notifications'));
    } catch (e) {
      setSettingsError(e instanceof Error && e.message.trim() ? e.message : 'The server did not answer.');
    }
  }, []);

  React.useEffect(() => {
    void loadSettings();
    void currentPermission().then(setPermission);
    void isSubscribed().then(setSubscribed);
  }, [loadSettings]);

  const patch = async (body: Record<string, unknown>) => {
    try {
      await api.patch('/api/settings/notifications', body);
      await loadSettings();
    } catch (e) {
      fail(e);
    }
  };

  /** Drafts are seeded at the moment of opening, so a poll landing mid-edit cannot move the wheels. */
  const openPicker = React.useCallback((s: NotificationSettings) => {
    setFromDraft(s.quietStart);
    setUntilDraft(s.quietEnd);
    setPicking(true);
  }, []);

  const mine = me ? partnerTint(me.partner) : null;
  const theirs = me ? partnerTint(otherPartner(me.partner)) : null;

  /**
   * WHEN THE ASK IS ALLOWED TO APPEAR — the rule this file's own header has documented since the
   * migration, finally read by the code under it.
   *
   * It used to render whenever permission was not `granted`, which is "on load, forever, on every
   * device that ever said no" — the exact thing the rule forbids. `justPaired` was being set in
   * `session.ts` and read nowhere.
   *
   *   just paired          the moment the card is for
   *   default, no sub      a device that has never been asked and never registered — a new phone
   *                        on an old pairing, which has the same "you have just arrived" shape
   *   denied               NOT an ask. A button that opens no prompt is a button that lies; the
   *                        blocked card says where the switch actually is instead.
   */
  const blocked = pushSupported() && permission === 'denied';
  const asksForPush = pushSupported() && permission !== 'granted' && !blocked && (justPaired || !subscribed);

  const shown = settings ?? FALLBACK;
  /** True while the card is drawn from `FALLBACK` — every control is off until a real load lands. */
  const stale = !settings;
  const isDefault = shown.quietStart === shown.defaults.quietStart && shown.quietEnd === shown.defaults.quietEnd;

  return (
    <Panel id="home">
      {/**
       * A MOMENT — the pairing is the one thing on this screen that happened rather than merely
       * being true, so it wears the garland. It is also the one titled-looking card with NO header
       * band: the two squares are its heading, and a red panel above them would put a third colour
       * between the couple and their own gels.
       */}
      {me && mine && theirs ? (
        <Card moment>
          <CardContent className="items-center gap-3 py-6">
            {/**
             * The pairing card is where the two identity colours come from — every dot, bar and
             * bubble elsewhere in the app is reading back to these two squares, so this is the one
             * place they have to be shown at full strength and side by side. They are passed as
             * `style` rather than as classes deliberately: they are literal identity values that a
             * later theming stage replaces in one file, not theme roles.
             *
             * EVERY GEL CARRIES ITS EDGE. B's fill is midnight #1E2532, which is also Tuxedo dark's
             * card colour — so a square drawn as fill alone is a square that is not there, and this
             * is the largest one in the app. The 1px hairline is the gel's own `line`, which is the
             * fill again wherever the fill already reads (so it costs nothing there) and lifts to a
             * legible edge exactly where it does not. The sheets say the same in `.avatar`:
             * `border: 1px solid var(--p-edge)` / `var(--p-line)`, in both looks.
             */}
            <View className="flex-row items-center gap-3">
              <View
                className="size-14 items-center justify-center rounded-md"
                style={{ backgroundColor: mine.fill, borderWidth: 1, borderColor: mine.line }}>
                <Text variant="heading" className="font-bold" style={{ color: mine.ink }}>
                  {initials(me.name)}
                </Text>
              </View>
              <Text
                className={cn(
                  'font-display text-[20px] leading-[20px]',
                  look === 'bouquet' ? 'italic text-gold-text' : 'text-muted-foreground',
                )}>
                &amp;
              </Text>
              <View
                className="size-14 items-center justify-center rounded-md"
                style={{ backgroundColor: theirs.fill, borderWidth: 1, borderColor: theirs.line }}>
                <Text variant="heading" className="font-bold" style={{ color: theirs.ink }}>
                  {initials(me.partnerName)}
                </Text>
              </View>
            </View>
            <Text className="text-center">
              You are <Text className="font-display font-semibold">{me.name}</Text> — playing with{' '}
              <Text className="font-display font-semibold">{me.partnerName}</Text>.
            </Text>

            {nameDraft === null ? (
              /**
               * The one ghost the two sheets cut differently, and they disagree about more than a
               * size: Bouquet's `.pairing .btn-ghost` is a 14px sentence at 600 with a whisker of
               * tracking, and Tuxedo's is a 10.5px tracked capital — a LABEL rather than a
               * sentence. Read off the look for that reason: one wording split between the rooms
               * would be the Tuxedo word standing in a Bouquet card, which is what the capture
               * showed. Same handler, same ghost, only the type moves.
               *
               * The COLOUR is no longer overridden here. `text-muted-foreground` is this app's
               * DISABLED ink, and in the dark rooms that is exactly what this read as; the ghost's
               * own ink (see `lib/edges.ts`) is a control's colour in all four.
               */
              <Button variant="ghost" size="sm" flush={false} onPress={() => setNameDraft(me.name)}>
                <Text
                  className={
                    look === 'bouquet'
                      ? 'text-[14px] font-semibold tracking-[0.56px]'
                      : 'text-[10.5px] font-bold uppercase tracking-[1.47px]'
                  }>
                  Change my name
                </Text>
              </Button>
            ) : (
              /* Capped and centred rather than run to the card's edge: on a desktop the card is
                 the full 880 column, and a name field eight hundred pixels wide for forty
                 characters is the "stretched phone" the design file names. */
              <View className="w-full max-w-[420px] flex-row flex-wrap items-center gap-2">
                <Input
                  className="min-w-[160px] flex-1"
                  value={nameDraft}
                  onChangeText={setNameDraft}
                  maxLength={40}
                  autoComplete="off"
                />
                <Button
                  disabled={!nameDraft.trim() || nameDraft.trim() === me.name}
                  reason={nameDraft.trim() ? 'That is already your name' : 'Type a name'}
                  onPress={async () => {
                    const name = nameDraft.trim();
                    setNameDraft(null);
                    if (name) await rename(name).catch(fail);
                  }}>
                  <Text>Save</Text>
                </Button>
                <Button variant="ghost" size="sm" flush={false} onPress={() => setNameDraft(null)}>
                  <Text>Cancel</Text>
                </Button>
              </View>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent>
          <CardHead>Before you say anything</CardHead>
          <CardDescription>
            Your private preference sheet. The drafter reads it before it plants anything for you, and it is where you
            list the names and places that get swapped out before a signal ever leaves this device.
          </CardDescription>
          <Button variant="secondary" className="self-start" onPress={() => openOverlay('prefs')}>
            <Text>Open preference sheet</Text>
          </Button>
        </CardContent>
      </Card>

      {blocked ? (
        /**
         * NO BUTTON THAT CANNOT WORK.
         *
         * Once a browser or a phone has recorded a denial, `requestPermission` resolves `denied`
         * without showing anything at all — so the old card offered an "Enable notifications"
         * button whose only possible outcome was the sentence "Notifications stayed off". The
         * switch is in the operating system now, and the only useful thing this card can do is say
         * where. All three platforms, because the app runs on all three and somebody reading this
         * does not think of themselves as being on one of them.
         */
        <Card>
          <CardContent>
            <CardHead>Notifications are blocked for this site</CardHead>
            <CardDescription>
              This device has already said no, and the app cannot ask again — the switch is in your own settings now.
              Nothing is lost either way: invites and reveals are all still here when you open Hunsy.
            </CardDescription>
            <View className="gap-1">
              <Text variant="small" className="text-muted-foreground">
                <Text className="font-semibold">Browser</Text> — the lock or ⓘ icon left of the address bar → Site
                settings → Notifications → Allow.
              </Text>
              <Text variant="small" className="text-muted-foreground">
                <Text className="font-semibold">iPhone and iPad</Text> — Settings → Notifications → Hunsy → Allow
                Notifications. Hunsy has to be on the Home Screen first.
              </Text>
              <Text variant="small" className="text-muted-foreground">
                <Text className="font-semibold">Android</Text> — Settings → Apps → Hunsy → Notifications.
              </Text>
            </View>
          </CardContent>
        </Card>
      ) : asksForPush ? (
        <Card>
          <CardContent>
            <CardHead>Turn on notifications</CardHead>
            <CardDescription>
              Invites and game events arrive as gentle pings. They never contain content — a module name and a count,
              nothing anyone can read off a lock screen.
            </CardDescription>
            <Button
              className="self-start"
              disabled={asking}
              onPress={async () => {
                setAsking(true);
                setAskResult('');
                const ok = await enablePush().catch(() => false);
                const next = await currentPermission();
                setPermission(next);
                setSubscribed(await isSubscribed());
                setAskResult(
                  ok
                    ? 'Subscribed — your partner’s devices just got a test ping.'
                    : next === 'default'
                      ? 'No answer from the browser yet. Look for a permission prompt — on iPhone, add Hunsy to the Home Screen first, then try again.'
                      : 'Notifications stayed off (permission not granted).',
                );
                setAsking(false);
              }}>
              <Text>{asking ? 'Asking your browser…' : 'Enable notifications'}</Text>
            </Button>
            {askResult ? (
              <Text variant="small" className="text-muted-foreground">
                {askResult}
              </Text>
            ) : null}
          </CardContent>
        </Card>
      ) : subscribed ? (
        <Card>
          <CardContent>
            <CardHead>Notifications on</CardHead>
            <CardDescription>This device gets pings. Prove the loop:</CardDescription>
            <Button
              variant="outline"
              className="self-start"
              onPress={async () => {
                const n = await sendTestPush().catch(() => 0);
                setPingResult(n > 0 ? `Ping sent to ${n} device(s).` : 'No subscribed devices on their side yet.');
              }}>
              <Text>Ping {me?.partnerName ?? 'them'}’s devices</Text>
            </Button>
            {pingResult ? (
              <Text variant="small" className="text-muted-foreground">
                {pingResult}
              </Text>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* The skeleton is for the FIRST attempt only. Once it has failed there is still a card to
          draw — the defaults, with every control off and a way to ask again — which is a better
          answer than a grey block that never resolves, and a far better one than no card at all. */}
      {stale && !settingsError ? <PanelSkeleton rows={1} /> : null}

      {!stale || settingsError ? (
        <Section title="Notification settings">
          {settingsError ? (
            <View className="flex-row flex-wrap items-center gap-2">
              <Text variant="small" className="text-destructive">
                Couldn’t load your settings
              </Text>
              <Text className="text-muted-foreground">·</Text>
              <Button variant="ghost" size="sm" flush={false} onPress={() => void loadSettings()}>
                <Text>Try again</Text>
              </Button>
            </View>
          ) : null}

          {/**
           * A SETTINGS LIST, not a stack of controls. Both sheets draw this the same way — one
           * hairline between rows, label left and control right — so the rows live in a container
           * of their own rather than as siblings of `CardContent`'s `gap-3`, which would put twelve
           * pixels of air where the rule goes.
           */}
          <View className={ROWS_CLASS}>
            <Row first label="Mute all nudges (invites and reveals still arrive)">
              <Switch
                value={shown.muted}
                disabled={stale}
                onChange={(muted) => patch({ muted })}
                accessibilityLabel="Mute all nudges"
              />
            </Row>

            {/**
             * The one setting that decides when the game may ping, read as a sentence rather than
             * as two boxes to type into. It was two `HH:MM` text fields, which asks somebody to
             * know the format, get the colon right and re-derive "so that is eleven hours" in
             * their head every time — for a value with 48 possible answers per end.
             *
             * The whole row is the control, with a chevron where the "Change" word was: on a list
             * of rows a bordered box inside one row reads as a different KIND of thing, and this is
             * the same "press it and it opens" the row above is.
             */}
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ expanded: picking, disabled: stale }}
              accessibilityLabel={`Quiet hours, ${shown.quietStart} to ${shown.quietEnd}. ${picking ? 'Close' : 'Open'} the picker.`}
              disabled={stale}
              onPress={() => (picking ? setPicking(false) : openPicker(shown))}
              className={cn(
                'border-border flex-row items-center gap-3 border-t py-3 active:opacity-70',
                stale && 'opacity-50',
              )}>
              <Text className="text-body min-w-0 flex-1 font-medium">
                Quiet <Text className="font-display font-semibold">{shown.quietStart}</Text>{' '}
                <Text className="text-muted-foreground">→</Text>{' '}
                <Text className="font-display font-semibold">{shown.quietEnd}</Text>
                <Text className="text-muted-foreground">
                  {' '}
                  · {spanLabel(spanMinutes(shown.quietStart, shown.quietEnd))}
                </Text>
              </Text>
              {/* A disclosure that TURNS. A chevron pointing the same way open and shut is a
                  chevron saying nothing about which of the two it is. */}
              <Text
                className="text-muted-foreground text-[21px] leading-[21px]"
                style={{ transform: [{ rotate: picking ? '90deg' : '0deg' }] }}>
                ›
              </Text>
            </Pressable>

            {/* Only where there is something to go back TO. On a window nobody has moved, "Reset
                to default" is a control whose only effect is to write the value already there. */}
            {!stale && !isDefault ? (
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                onPress={() =>
                  void patch({ quietStart: shown.defaults.quietStart, quietEnd: shown.defaults.quietEnd })
                }>
                <Text>
                  Reset to default ({shown.defaults.quietStart} → {shown.defaults.quietEnd})
                </Text>
              </Button>
            ) : null}

            {picking ? (
              <View className="py-3">
                <WheelPicker
                  columns={[
                    { label: 'From', value: fromDraft, values: TIMES, onChange: setFromDraft, nearest: nearestIndex },
                    { label: 'Until', value: untilDraft, values: TIMES, onChange: setUntilDraft, nearest: nearestIndex },
                  ]}
                  readout={`${spanLabel(spanMinutes(fromDraft, untilDraft))} of quiet`}
                  onCancel={() => setPicking(false)}
                  onDone={() => {
                    setPicking(false);
                    void patch({ quietStart: fromDraft, quietEnd: untilDraft });
                  }}
                />
              </View>
            ) : null}
          </View>

          <Text variant="small" className="text-muted-foreground">
            Nudges come from the game, at most one a day, never during quiet hours — and never phrased as{' '}
            {me?.partnerName ?? 'your partner'} waiting on you.
          </Text>
        </Section>
      ) : null}

      {/**
       * LOOK — the two things that change what the app looks like, in one place.
       *
       * The Bouquet/Tuxedo switch used to live at the foot of "Notification settings", which is a
       * card about when the game may ping somebody: the one control in the app that changes which
       * ROOM you are standing in was filed under notifications. The light/dark toggle was in the
       * shell chrome and nowhere else. They are one decision, so they sit together.
       */}
      <Section title="Look">
        <View className={ROWS_CLASS}>
          <Row first label="Theme">
            <Segmented options={THEME_OPTIONS} value={look} onChange={setLook} accessibilityLabel="Theme" />
          </Row>
          <Row label="Light or dark">
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </Row>
          {/* THE DEBUG PICKER, LABELLED AS ONE. A bare "Switch" in the panel header sat exactly
              where a feature would, unexplained, and it is only ever offered when the server's
              debug identity picker is on. It says what it switches now, and it says it quietly. */}
          {identities.length ? (
            <Row label={`Playing as ${me?.name ?? '—'}`}>
              <Button variant="ghost" size="sm" flush={false} onPress={() => setSession('pick')}>
                <Text className="text-muted-foreground">Switch player</Text>
              </Button>
            </Row>
          ) : null}
        </View>
      </Section>

      <Section title="How Hunsy works">
        <Text className="text-muted-foreground">
          Each tab is one part of the game. The quiet rule underneath all of them: anything you tell the app privately
          is never shown to your partner as you said it — it becomes anonymous missions, planted ideas, gentle nudges.
          The input box is safe.
        </Text>
      </Section>
    </Panel>
  );
}

/** Half-hour steps: 48 of them, which is the resolution a nudge window is ever chosen at. */
const STEP_MINUTES = 30;
const TIMES = Array.from({ length: (24 * 60) / STEP_MINUTES }, (_, i) => {
  const mins = i * STEP_MINUTES;
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
});

/** Nearest step to an arbitrary `HH:MM` — the server accepts any minute, the wheel offers 48. */
function nearestIndex(value: string): number {
  const i = Math.round(minutesOf(value) / STEP_MINUTES);
  return Math.min(TIMES.length - 1, Math.max(0, i));
}
